import { cn } from '@exam-room/utils';
import * as Sentry from '@sentry/react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';

interface PDFViewerProps {
  url: string;
  className?: string;
  height?: number | string;
}

function PDFViewer({ url, className = '', height = 600 }: PDFViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [scale, setScale] = useState(1);
  const scrollRatioRef = useRef<number>(0);
  const [isRendering, setIsRendering] = useState(false);
  const lastPageRef = useRef(currentPage); // To prevent page number from flickering when zooming

  // Observe container width
  useEffect(() => {
    if (!containerRef.current) return;
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    // Set the PDF.js worker script location.
    // This file (pdf.worker.min.mjs) must be in the /public folder so it can be loaded directly by the browser at runtime.
    // Placing it in /public ensures the worker can be fetched via an absolute URL, allowing PDF.js to offload heavy PDF parsing to a separate thread for better performance and UI responsiveness.

    // Alternative: fetch it directly from https://unpkg.com/pdfjs-dist@5.3.93/build/pdf.worker.min.mjs
    // For development, use above url to fetch the worker file
    GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'; // Currently using 5.3.93 according to the installed `pdfjs-dist` version

    let isCanceled = false;
    let renderLock = false;

    // Save scroll ratio before rendering (on scale change)
    if (containerRef.current) {
      const ref = containerRef.current;
      if (ref.scrollHeight > 0) {
        scrollRatioRef.current = ref.scrollTop / ref.scrollHeight;
      } else {
        scrollRatioRef.current = 0;
      }
    }

    lastPageRef.current = currentPage;
    setIsRendering(true);

    const renderPDF = async () => {
      const ref = containerRef.current;
      if (!ref || renderLock) return;
      renderLock = true;
      // Always clear container before rendering
      while (ref.firstChild) {
        ref.removeChild(ref.firstChild);
      }

      let pdf;
      try {
        pdf = await getDocument(url).promise;
      } catch (error) {
        console.error('Failed to load PDF:', error);
        setIsRendering(false);
        return;
      }

      if (isCanceled) return;
      setNumPages(pdf.numPages);

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        if (isCanceled) break;
        const page = await pdf.getPage(pageNum);

        const containerWidth = ref.offsetWidth || 600;
        const viewport = page.getViewport({ scale: 1 });
        const scaleForViewport = containerWidth / viewport.width;
        // Use scale state for zooming
        const scaledViewport = page.getViewport({ scale: scaleForViewport * scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) continue;
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        canvas.style.width = scaledViewport.width + 'px'; // Set width in px for zoom
        canvas.style.display = 'block';

        try {
          await page.render({ canvasContext: context, viewport: scaledViewport }).promise;
          canvas.setAttribute('data-page', String(pageNum));
          if (!isCanceled) {
            ref.appendChild(canvas);
          }
        } catch (error) {
          console.error(`Error rendering page ${pageNum}:`, error);
          Sentry.captureException(error, {
            tags: {
              feature: 'pdf-rendering',
              page: pageNum,
            },
          });
        }
      }
      renderLock = false;

      // Restore scroll position after rendering
      if (ref && ref.scrollHeight > 0) {
        ref.scrollTop = scrollRatioRef.current * ref.scrollHeight;
      }
      setIsRendering(false);
    };

    if (containerWidth > 0) {
      renderPDF();
    }
    return () => {
      isCanceled = true;
    };
  }, [url, containerWidth, scale]);

  // Scroll event to update current page
  useEffect(() => {
    const onScroll = () => {
      const ref = containerRef.current;
      if (!ref) return;
      const canvases = Array.from(ref.querySelectorAll('canvas'));
      const scrollTop = ref.scrollTop;

      let foundPage = canvases.length; // Default to last page
      for (let i = 0; i < canvases.length; i++) {
        const canvas = canvases[i];
        if (canvas.offsetTop + canvas.height / 2 > scrollTop) {
          foundPage = i + 1;
          break;
        }
      }

      // If scrolled to (or near) the bottom, display the last page number
      if (canvases.length > 0 && ref.scrollTop + ref.clientHeight >= ref.scrollHeight - 2) {
        foundPage = canvases.length;
      }

      foundPage = Math.max(1, Math.min(foundPage, numPages));
      setCurrentPage(foundPage);
    };
    const ref = containerRef.current;
    if (ref) ref.addEventListener('scroll', onScroll);
    return () => {
      if (ref) ref.removeEventListener('scroll', onScroll);
    };
  }, [numPages]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  return (
    <div className={cn('border-border-divider w-full rounded-xl border', className)}>
      {/* Header */}
      <div className='bg-grey-900 flex items-center justify-center gap-4 rounded-t-xl py-3 text-xl text-white select-none'>
        <span className='rounded px-3 py-1'>{isRendering ? lastPageRef.current : currentPage}</span>
        <span className='mx-1'>/</span>
        <span>{numPages}</span>
        <span className='text-grey-400 mx-3'>|</span>
        <button
          onClick={handleZoomOut}
          className='cursor-pointer border-none bg-none text-2xl text-white'
        >
          −
        </button>
        <span className='rounded px-3 py-1'>{Math.round(scale * 100)}%</span>
        <button
          onClick={handleZoomIn}
          className='cursor-pointer border-none bg-none text-2xl text-white'
        >
          +
        </button>
      </div>

      {/* PDF Pages */}
      <div
        className='bg-grey-700 w-full overflow-x-auto overflow-y-auto'
        style={{ height, visibility: isRendering ? 'hidden' : 'visible' }} // prevent scroll position shift when zooming
        ref={containerRef}
      />
    </div>
  );
}

export default PDFViewer;
